import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { type APIResponse, type ReportsMatterStatusCount } from "@/lib/types";

export type RetrieveReportsMatterStatusCountInput = {
  category: string[];
  incidentTypeIds?: string[];
  claimTypeIds?: string[];
};

type MatterStatusCountApiData = {
  metrics: { totalMatters: number; totalStatuses: number };
  rows: Array<{
    count: number;
    matterStatusCategory: string;
    matterStatusLabel: string;
  }>;
};

type MatterStatusCountApiResponse = Omit<
  APIResponse<MatterStatusCountApiData>,
  "pagination"
> & {
  pagination?: { hasMore?: boolean };
};

const PAGE_SIZE = 250;
const MAX_PAGES = 1000;

export const retrieveReportsMatterStatusCount = async (
  data: RetrieveReportsMatterStatusCountInput,
) => {
  const rows: ReportsMatterStatusCount[] = [];
  let pageNumber = 1;
  let meta: APIResponse<ReportsMatterStatusCount[]>["meta"] | undefined;
  let hasMore = false;

  do {
    const response =
      await authenticatedFastApiAxiosInstance.post<MatterStatusCountApiResponse>(
        "reports/v2/matters-per-status/query",
        {
          filters: {
            matterStatusCategories: data.category,
            incidentTypeIds: data.incidentTypeIds ?? [],
            claimTypeIds: data.claimTypeIds ?? [],
          },
          pageNumber,
          pageSize: PAGE_SIZE,
        },
      );

    meta = response.data.meta;
    rows.push(
      ...response.data.data.rows.map(
        (row): ReportsMatterStatusCount => ({
          category: row.matterStatusCategory,
          count: row.count,
          label: row.matterStatusLabel,
        }),
      ),
    );
    hasMore = response.data.pagination?.hasMore === true;
    pageNumber += 1;
  } while (hasMore && pageNumber <= MAX_PAGES);

  if (hasMore) {
    throw new Error(
      `The matters per status report exceeded the ${MAX_PAGES}-page retrieval limit.`,
    );
  }

  return {
    data: rows,
    meta,
  } satisfies APIResponse<ReportsMatterStatusCount[]>;
};
